import React, { useCallback } from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { SpotlightTarget } from '@atlaskit/onboarding';
import type { SVGProps } from '@atlaskit/icon/svg';
import { Box, Inline } from '@atlaskit/primitives';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import { default as OldButton } from '@atlaskit/button/custom-theme-button';

import { expValEquals } from '@confluence/feature-experiments';
import { h300Compiled } from '@confluence/typography-placeholder';

import { FeedTypeSelector } from '../FeedTypeSelector';
import { FeedType } from '../useFeedTypeSelector';
import { FollowingFeedSortByValue, PopularFeedTimeGranularityValue } from '../Feed';

export const i18n = defineMessages({
	feedTitle: {
		id: 'feed.title',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: "Discover what's happening",
		description: 'Feed title',
	},
	manageFeed: {
		id: 'feed.header.manage.following',
		defaultMessage: 'Edit feed',
		description: 'Button text that opens a modal that allows the user to configure settings',
	},
	sortByRelevant: {
		id: 'feed.header.sortBy.following.relevant',
		defaultMessage: 'Sort by: Most relevant',
		description:
			'Following feed sorting option which will sort the feed by relevance, showing the most relevant first.',
	},
	sortByRecent: {
		id: 'feed.header.sortBy.following.recent',
		defaultMessage: 'Sort by: Last activity',
		description:
			'Following feed sorting option which will sort the feed by last activity date, showing the most recent first.',
	},
	day: {
		id: 'feed.popular.day',
		defaultMessage: 'Today',
		description: 'Popular Feed Time Range Option for past day',
	},
	week: {
		id: 'feed.popular.week',
		defaultMessage: 'Past 7 days',
		description: 'Popular Feed Time Range Option for past week',
	},
	twoWeeks: {
		id: 'feed.popular.twoWeeks',
		defaultMessage: 'Past 14 days',
		description: 'Popular Feed Time Range Option for past 2 weeks',
	},
	month: {
		id: 'feed.popular.month',
		defaultMessage: 'Past 30 days',
		description: 'Popular Feed Time Range Option for past month',
	},
	year: {
		id: 'feed.popular.year',
		defaultMessage: 'Past year',
		description: 'Popular Feed Time Range Option for past year',
	},
	popularDay: {
		id: 'feed.popular.dropdown.prefix.day',
		defaultMessage: 'Popular: Today',
		description:
			"Popular Feed Time Range Option for past day with 'Popular' prefix for when dropdown is closed",
	},
	popularWeek: {
		id: 'feed.popular.dropdown.prefix.week',
		defaultMessage: 'Popular: Past 7 days',
		description:
			"Popular Feed Time Range Option for past week with 'Popular' prefix for when dropdown is closed",
	},
	popularTwoWeeks: {
		id: 'feed.popular.dropdown.prefix.twoWeeks',
		defaultMessage: 'Popular: Past 14 days',
		description:
			"Popular Feed Time Range Option for past two weeks with 'Popular' prefix for when dropdown is closed",
	},
	popularMonth: {
		id: 'feed.popular.dropdown.prefix.month',
		defaultMessage: 'Popular: Past 30 days',
		description:
			"Popular Feed Time Range Option for past month with 'Popular' prefix for when dropdown is closed",
	},
	popularYear: {
		id: 'feed.popular.dropdown.prefix.year',
		defaultMessage: 'Popular: Past year',
		description:
			"Popular Feed Time Range Option for past year with 'Popular' prefix for when dropdown is closed",
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeedTitleAndActions = styled.div({
	position: 'relative',
	justifyContent: 'space-between',
	display: 'flex',
	flexWrap: 'wrap',
	marginBottom: token('space.200', '16px'),
	flexDirection: 'column',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectorAndActions = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	flex: 1,
	whiteSpace: 'nowrap',
	marginTop: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeedTitleHeader = styled.div({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	height: token('space.400', '32px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FeedTitle = styled.h2({
	marginRight: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		...h300Compiled,
		marginTop: '0px',
		textTransform: 'uppercase',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpotlightContainer = styled.div({
	position: 'absolute',
	top: '0px',
	left: token('space.negative.050', '-4px'),
	height: '100%',
	width: '100%',
	padding: token('space.050', '4px'),
	pointerEvents: 'none',
});

const linkButtonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			fontWeight: 400,
		},
		spinnerStyles,
	};
};

type FeedActionsDropdownComponentProps = {
	openActivityFilterModal: () => void;
	renderPopularFeed: boolean;
	feedType: FeedType | string;
	setFeedType: (feedType: FeedType | string) => void;
	customPanels?: {
		title: string;
		key: string;
		icon?: (props: SVGProps) => JSX.Element;
		content: JSX.Element;
	}[];
	popularFeedPillRef: React.RefObject<HTMLButtonElement>;
	followingFeedPillRef: React.RefObject<HTMLButtonElement>;
	popularFeedTimeGranularity: PopularFeedTimeGranularityValue;
	setPopularFeedTimeGranularity: React.Dispatch<
		React.SetStateAction<PopularFeedTimeGranularityValue>
	>;
	followingFeedSortBy: FollowingFeedSortByValue;
	setFollowingFeedSortBy: React.Dispatch<React.SetStateAction<FollowingFeedSortByValue>>;
};

export const FeedHeader: FC<FeedActionsDropdownComponentProps> = ({
	openActivityFilterModal,
	renderPopularFeed,
	feedType,
	setFeedType,
	customPanels,
	popularFeedPillRef,
	followingFeedPillRef,
	popularFeedTimeGranularity,
	setPopularFeedTimeGranularity,
	followingFeedSortBy,
	setFollowingFeedSortBy,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleManageButtonClick = useCallback(() => {
		openActivityFilterModal();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'item',
				actionSubjectId: 'editFeed',
				source: 'homeScreen',
			},
		}).fire();
	}, [createAnalyticsEvent, openActivityFilterModal]);

	const getPopularFeedTimeGranularityDropDownText = (): string => {
		switch (popularFeedTimeGranularity) {
			case PopularFeedTimeGranularityValue.Day:
				return formatMessage(i18n.popularDay);
			case PopularFeedTimeGranularityValue.Week:
				return formatMessage(i18n.popularWeek);
			case PopularFeedTimeGranularityValue.Month:
				return formatMessage(i18n.popularMonth);
			case PopularFeedTimeGranularityValue.Year:
				return formatMessage(i18n.popularYear);
			default:
				return formatMessage(i18n.popularTwoWeeks);
		}
	};

	const getFollowingFeedSortByDropDownText = (): string => {
		switch (followingFeedSortBy) {
			case FollowingFeedSortByValue.Recent:
				return formatMessage(i18n.sortByRecent);
			default:
				return formatMessage(i18n.sortByRelevant);
		}
	};

	const handleDropdownChange = useCallback(
		(
			type: 'popularTimeGranularity' | 'followingSortBy',
			value: PopularFeedTimeGranularityValue | FollowingFeedSortByValue,
			setValue: (value: any) => void,
		) => {
			setValue(value);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'item',
					actionSubjectId: type,
					source: 'homeScreen',
					attributes: {
						[type === 'popularTimeGranularity' ? 'timeGranularity' : 'sortBy']: value,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent],
	);

	const handlePopularFeedTimeGranularityDropdownChange = useCallback(
		(timeGranularity: PopularFeedTimeGranularityValue) => {
			handleDropdownChange(
				'popularTimeGranularity',
				timeGranularity,
				setPopularFeedTimeGranularity,
			);
		},
		[handleDropdownChange, setPopularFeedTimeGranularity],
	);

	const handleFollowingFeedSortByDropdownChange = useCallback(
		(sortBy: FollowingFeedSortByValue) => {
			handleDropdownChange('followingSortBy', sortBy, setFollowingFeedSortBy);
		},
		[handleDropdownChange, setFollowingFeedSortBy],
	);

	const handleDropdownOpenChange = (
		type: 'popularTimeGranularity' | 'followingSortBy',
		isOpen: boolean,
	) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'toggled',
				actionSubject: 'dropdown',
				actionSubjectId: type,
				source: 'homeScreen',
				attributes: {
					isOpen,
				},
			},
		}).fire();
	};

	const handlePopularFeedTimeGranularityDropdownOpenChange = (isOpen: boolean) => {
		handleDropdownOpenChange('popularTimeGranularity', isOpen);
	};

	const handleFollowingFeedSortByDropdownOpenChange = (isOpen: boolean) => {
		handleDropdownOpenChange('followingSortBy', isOpen);
	};

	const renderActions = () => {
		if (
			feedType === FeedType.POPULAR &&
			expValEquals('confluence_popular_feed_time_ranges_experiment', 'cohort', 'test')
		) {
			return (
				<Box>
					<DropdownMenu
						onOpenChange={(onOpenChangeAttributes) =>
							handlePopularFeedTimeGranularityDropdownOpenChange(onOpenChangeAttributes.isOpen)
						}
						trigger={getPopularFeedTimeGranularityDropDownText()}
						shouldRenderToParent
					>
						<DropdownItemGroup>
							<DropdownItem
								isSelected={popularFeedTimeGranularity === PopularFeedTimeGranularityValue.Day}
								onClick={() =>
									handlePopularFeedTimeGranularityDropdownChange(
										PopularFeedTimeGranularityValue.Day,
									)
								}
							>
								<FormattedMessage {...i18n.day} />
							</DropdownItem>
							<DropdownItem
								isSelected={popularFeedTimeGranularity === PopularFeedTimeGranularityValue.Week}
								onClick={() =>
									handlePopularFeedTimeGranularityDropdownChange(
										PopularFeedTimeGranularityValue.Week,
									)
								}
							>
								<FormattedMessage {...i18n.week} />
							</DropdownItem>
							<DropdownItem
								isSelected={popularFeedTimeGranularity === PopularFeedTimeGranularityValue.Default}
								onClick={() =>
									handlePopularFeedTimeGranularityDropdownChange(
										PopularFeedTimeGranularityValue.Default,
									)
								}
							>
								<FormattedMessage {...i18n.twoWeeks} />
							</DropdownItem>
							<DropdownItem
								isSelected={popularFeedTimeGranularity === PopularFeedTimeGranularityValue.Month}
								onClick={() =>
									handlePopularFeedTimeGranularityDropdownChange(
										PopularFeedTimeGranularityValue.Month,
									)
								}
							>
								<FormattedMessage {...i18n.month} />
							</DropdownItem>
							<DropdownItem
								isSelected={popularFeedTimeGranularity === PopularFeedTimeGranularityValue.Year}
								onClick={() =>
									handlePopularFeedTimeGranularityDropdownChange(
										PopularFeedTimeGranularityValue.Year,
									)
								}
							>
								<FormattedMessage {...i18n.year} />
							</DropdownItem>
						</DropdownItemGroup>
					</DropdownMenu>
				</Box>
			);
		} else if (
			feedType === FeedType.FOLLOWING &&
			expValEquals('cc_smarts_following_feed_dropdown_experiment', 'cohort', 'test')
		) {
			return (
				<Inline space="space.100">
					<DropdownMenu
						onOpenChange={(onOpenChangeAttributes) =>
							handleFollowingFeedSortByDropdownOpenChange(onOpenChangeAttributes.isOpen)
						}
						trigger={getFollowingFeedSortByDropDownText()}
						shouldRenderToParent
					>
						<DropdownItemGroup>
							<DropdownItem
								isSelected={followingFeedSortBy === FollowingFeedSortByValue.Relevant}
								onClick={() =>
									handleFollowingFeedSortByDropdownChange(FollowingFeedSortByValue.Relevant)
								}
							>
								<FormattedMessage {...i18n.sortByRelevant} />
							</DropdownItem>
							<DropdownItem
								isSelected={followingFeedSortBy === FollowingFeedSortByValue.Recent}
								onClick={() =>
									handleFollowingFeedSortByDropdownChange(FollowingFeedSortByValue.Recent)
								}
							>
								<FormattedMessage {...i18n.sortByRecent} />
							</DropdownItem>
						</DropdownItemGroup>
					</DropdownMenu>
					<Button onClick={handleManageButtonClick}>
						<FormattedMessage {...i18n.manageFeed} />
					</Button>
				</Inline>
			);
		} else if (feedType === FeedType.FOLLOWING) {
			return (
				<OldButton
					theme={linkButtonTheme}
					onClick={handleManageButtonClick}
					testId="manage-feed-button"
				>
					<FormattedMessage {...i18n.manageFeed} />
				</OldButton>
			);
		}
	};

	return (
		<FeedTitleAndActions data-testid="feed-header">
			<SpotlightTarget name="home-activity">
				<SpotlightContainer />
			</SpotlightTarget>
			<FeedTitleHeader>
				<FeedTitle>
					<FormattedMessage {...i18n.feedTitle} />
				</FeedTitle>
				{renderActions()}
			</FeedTitleHeader>
			<SelectorAndActions>
				{renderPopularFeed && (
					<FeedTypeSelector
						selectedFeedType={feedType}
						setSelectedFeedType={setFeedType}
						customPanels={customPanels}
						popularFeedPillRef={popularFeedPillRef}
						followingFeedPillRef={followingFeedPillRef}
					/>
				)}
			</SelectorAndActions>
		</FeedTitleAndActions>
	);
};
